import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { ProgressSpinner } from "primereact/progressspinner";
import { useCallback, useRef, useState } from "react";
import * as client from "../../../client";
import { useToast } from "../../../context-providers/Toast";
import { SafeReportResponse } from "../Conversion";
import {
  useDeleteEmailThread,
  useEmailThreads,
  useUploadEmailThread,
} from "../data";
import styles from "./EmailCard.module.css";
import { EmailThreadModal, getThreadAttachmentCount } from "./EmailThreadModal";

const INBOUND_DOMAIN = "inbound.luminorrisk.com";

interface EmailCardProps {
  specialtyPropertyInfo: SafeReportResponse;
}

/** Extract a short subject from the first message of a thread. */
function getThreadSubject(thread: client.EmailThread): string {
  if (thread.messages.length === 0) return "(No subject)";
  return thread.messages[0]?.subject || "(No subject)";
}

/** Collect unique participant names/emails across all messages in a thread. */
function getThreadParticipants(thread: client.EmailThread): string {
  const participants = new Set<string>();
  for (const msg of thread.messages) {
    if (msg.from) participants.add(msg.from);
  }
  return Array.from(participants).join(", ");
}

/** Get the date of the latest message in a thread. */
function getLatestDate(thread: client.EmailThread): string {
  if (thread.messages.length === 0) return "";
  const last = thread.messages[thread.messages.length - 1];
  const dateStr = last?.date ?? "";
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

export const EmailCard: React.FC<EmailCardProps> = ({
  specialtyPropertyInfo,
}) => {
  const reportId = specialtyPropertyInfo.report_id;
  const emailThreads = specialtyPropertyInfo.report_json.email_threads ?? {};
  const hasThreads = Object.keys(emailThreads).length > 0;
  const { data, isLoading, isError } = useEmailThreads(reportId, hasThreads);
  const toast = useToast();

  const [selectedThread, setSelectedThread] =
    useState<client.EmailThread | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounter = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadMutation = useUploadEmailThread(reportId);
  const deleteMutation = useDeleteEmailThread(reportId);

  const threads = data?.threads ?? [];

  const reportEmail = `sub+${reportId}@${INBOUND_DOMAIN}`;

  const submitFiles = useCallback(
    (files: File[]) => {
      const valid = files.filter((f) => {
        const name = f.name.toLowerCase();
        return name.endsWith(".eml") || name.endsWith(".msg");
      });

      if (valid.length === 0) {
        toast.show({
          severity: "warn",
          summary: "Unsupported file",
          detail: "Please select .eml or .msg files",
          life: 3000,
        });
        return;
      }

      uploadMutation.mutate(valid, {
        onSuccess: (result) => {
          if (!result.was_new) {
            toast.show({
              severity: "info",
              summary: "Already linked",
              detail: "This email thread is already attached to the report",
              life: 3000,
            });
          } else {
            toast.show({
              severity: "success",
              summary: "Thread linked",
              detail: `Added ${result.eml_count} email(s) to the report`,
              life: 3000,
            });
          }
        },
        onError: () => {
          toast.show({
            severity: "error",
            summary: "Upload failed",
            detail: "Could not link email thread. Please try again.",
            life: 5000,
          });
        },
      });
    },
    [uploadMutation, toast]
  );

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (dragCounter.current === 1) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragOver(false);
      submitFiles(Array.from(e.dataTransfer.files));
    },
    [submitFiles]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        submitFiles(Array.from(e.target.files));
      }
      e.target.value = "";
    },
    [submitFiles]
  );

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(reportEmail);
      toast.show({
        severity: "success",
        summary: "Copied",
        detail: "Email address copied to clipboard",
        life: 3000,
      });
    } catch {
      toast.show({
        severity: "error",
        summary: "Copy failed",
        detail: "Could not copy to clipboard",
        life: 3000,
      });
    }
  };

  const handleRowClick = (thread: client.EmailThread) => {
    setSelectedThread(thread);
    setModalVisible(true);
  };

  const handleDeleteThread = useCallback(
    (thread: client.EmailThread) => {
      const subject = getThreadSubject(thread);
      confirmDialog({
        message: `Remove "${subject}" from this report? Linked .eml files will be deleted. This cannot be undone.`,
        header: "Delete Email Thread",
        icon: "pi pi-exclamation-triangle",
        defaultFocus: "reject",
        acceptClassName: "p-button-danger",
        acceptLabel: "Delete",
        rejectLabel: "Cancel",
        accept: () => {
          deleteMutation.mutate(thread.thread_url, {
            onSuccess: () => {
              toast.show({
                severity: "success",
                summary: "Thread removed",
                detail: "Email thread deleted from the report",
                life: 3000,
              });
            },
            onError: () => {
              toast.show({
                severity: "error",
                summary: "Delete failed",
                detail: "Could not delete email thread. Please try again.",
                life: 5000,
              });
            },
          });
        },
      });
    },
    [deleteMutation, toast]
  );

  const copyButton = (
    <div className={styles.buttonContainer}>
      <Button
        label="Copy Email Address (for BCC)"
        icon="pi pi-copy"
        severity="secondary"
        raised
        text
        onClick={handleCopyEmail}
        tooltip={reportEmail}
        tooltipOptions={{ position: "bottom" }}
      />
    </div>
  );

  const dropBox = (
    <div
      className={`${styles.dropBox} ${isDragOver ? styles.dropBoxActive : ""}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".eml,.msg"
        multiple
        onChange={handleFileInput}
        className={styles.hiddenInput}
      />
      {uploadMutation.isPending ? (
        <>
          <ProgressSpinner
            style={{ width: "24px", height: "24px" }}
            strokeWidth="4"
          />
          <span>Linking email thread...</span>
        </>
      ) : (
        <>
          <i className="pi pi-cloud-upload" />
          <span>
            Drag emails here or <strong>click to browse</strong> for .eml/.msg
            files
          </span>
        </>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <Card title="Emails" style={{ position: "relative" }}>
        {copyButton}
        {dropBox}
        <div className={styles.loadingContainer}>
          <ProgressSpinner
            style={{ width: "40px", height: "40px" }}
            strokeWidth="4"
          />
        </div>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card title="Emails" style={{ position: "relative" }}>
        {copyButton}
        {dropBox}
        <div className={styles.emptyState}>
          <i
            className="pi pi-exclamation-triangle"
            style={{ fontSize: "2rem", color: "var(--red-500)" }}
          />
          <span>Failed to load email threads. Please try again.</span>
        </div>
      </Card>
    );
  }

  if (threads.length === 0) {
    return (
      <Card title="Emails" style={{ position: "relative" }}>
        {copyButton}
        {dropBox}
        <div className={styles.emptyState}>
          <i className={`pi pi-envelope ${styles.emptyStateIcon}`} />
          <span>No email threads found for this report.</span>
          <span style={{ fontSize: "0.85rem" }}>
            CC or send emails to <strong>{reportEmail}</strong> to track threads
            here.
          </span>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Emails" style={{ position: "relative" }}>
      {copyButton}
      {dropBox}
      <div className={styles.container}>
        <DataTable
          value={threads}
          selectionMode="single"
          onRowClick={(e) => handleRowClick(e.data as client.EmailThread)}
          rowClassName={() => styles.threadRow}
          rowHover
          stripedRows
        >
          <Column
            header="Subject"
            body={(thread: client.EmailThread) => (
              <span className={styles.subjectColumn}>
                {getThreadSubject(thread)}
              </span>
            )}
            style={{ minWidth: "250px" }}
          />
          <Column
            header="Participants"
            body={(thread: client.EmailThread) => (
              <span className={styles.participantChip}>
                {getThreadParticipants(thread)}
              </span>
            )}
            style={{ minWidth: "200px" }}
          />
          <Column
            header="Date"
            body={(thread: client.EmailThread) => getLatestDate(thread)}
            style={{ width: "120px" }}
          />
          <Column
            header="Messages"
            body={(thread: client.EmailThread) => (
              <span className={styles.messageCount}>
                <i className="pi pi-comments" />
                {thread.messages.length}
              </span>
            )}
            style={{ width: "100px" }}
          />
          <Column
            header="Attachments"
            body={(thread: client.EmailThread) => {
              const count = getThreadAttachmentCount(thread.messages);
              if (count === 0) return null;
              return (
                <span className={styles.messageCount}>
                  <i className="pi pi-paperclip" />
                  {count}
                </span>
              );
            }}
            style={{ width: "120px" }}
          />
          <Column
            header=""
            body={(thread: client.EmailThread) => (
              <Button
                icon="pi pi-trash"
                severity="danger"
                text
                rounded
                aria-label="Delete email thread"
                tooltip="Delete thread"
                tooltipOptions={{ position: "left" }}
                disabled={
                  deleteMutation.isPending &&
                  deleteMutation.variables === thread.thread_url
                }
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteThread(thread);
                }}
              />
            )}
            style={{ width: "60px" }}
          />
        </DataTable>
      </div>
      <EmailThreadModal
        visible={modalVisible}
        onHide={() => {
          setModalVisible(false);
          setSelectedThread(null);
        }}
        thread={selectedThread}
        reportId={reportId}
      />
    </Card>
  );
};
